import React, { useState, useEffect, useCallback } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { Song } from "../../../Entities/SongModel";
import { SplitMode, SplitPageState } from "../../../utils/splitPageUtils";
import {
  computeDOMSplitIntersections,
  calculateRowMetrics,
  measureRowMetrics,
  RowMetrics,
  SplitIntersectionIndicator,
} from "../../../utils/splitIntersectionUtils";
import { SongBody } from "./SongBody";
import { SongCapoKeyBar } from "./SongCapoKeyBar";
import { SongControlsDesktop } from "./SongControlsDesktop";
import { SongControlsMobile } from "./SongControlsMobile";
import { SplitForegroundIndicator } from "./SplitForegroundIndicator";
import { SongContentTogglesProps } from "./SongContentToggles";

interface SongSplitViewProps extends SongContentTogglesProps {
  song: Song;
  fontChange: number;
  isHebrew: boolean;
  isScrolling: boolean;
  scrollSpeed: number[];
  mobileSettingsOpen: boolean;
  splitMode: SplitMode;
  preferredSplitMode?: SplitMode;
  splitPageState: SplitPageState;
  totalPages: number;
  isFullscreen: boolean;
  lyricsCardRef: React.RefObject<HTMLDivElement>;
  lyricsRef: React.MutableRefObject<HTMLDivElement | null>;
  paneRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  onToggleScroll: () => void;
  onToggleMobileSettings: () => void;
  onSpeedChange: (e: Event | null, newVal: number | number[]) => void;
  onSplitModeChange: (
    event: React.MouseEvent<HTMLElement> | null,
    newMode: SplitMode | null
  ) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  onIncreaseFont: () => void;
  onDecreaseFont: () => void;
  onCopyChords: () => void;
  onToggleFullscreen: () => void;
  onPaneScroll: (index: number) => void;
  applyUnifiedScrollDelta?: (deltaY: number) => boolean;
  showSplitIndicators?: boolean;
  onToggleSplitIndicators?: () => void;
}

export const SongSplitView: React.FC<SongSplitViewProps> = ({
  song,
  fontChange,
  isHebrew,
  visibleContent,
  onVisibleContentChange,
  isScrolling,
  scrollSpeed,
  mobileSettingsOpen,
  splitMode,
  preferredSplitMode,
  splitPageState,
  totalPages,
  isFullscreen,
  lyricsCardRef,
  lyricsRef,
  paneRefs,
  onToggleScroll,
  onToggleMobileSettings,
  onSpeedChange,
  onSplitModeChange,
  onPrevPage,
  onNextPage,
  onIncreaseFont,
  onDecreaseFont,
  onCopyChords,
  onToggleFullscreen,
  onPaneScroll,
  applyUnifiedScrollDelta,
  showSplitIndicators = true,
  onToggleSplitIndicators,
}) => {
  const [indicators, setIndicators] = useState<SplitIntersectionIndicator[]>([]);

  // Compute indicators using computeDOMSplitIntersections
  const updateIndicators = useCallback(() => {
    if (splitMode <= 1 || !paneRefs.current || paneRefs.current.length < 2) {
      setIndicators([]);
      return;
    }
    const hasRealDOMLayout =
      paneRefs.current[0] &&
      Array.from(
        paneRefs.current[0].querySelectorAll<HTMLElement>(
          '.song-row, [data-testid="song-row"]'
        )
      ).some(
        (el) =>
          el.offsetHeight > 0 ||
          (typeof el.getBoundingClientRect === "function" &&
            el.getBoundingClientRect().height > 0)
      );

    let rowMetrics: RowMetrics[] = [];
    if (hasRealDOMLayout && paneRefs.current[0]) {
      rowMetrics = measureRowMetrics(paneRefs.current[0]);
    }
    if (rowMetrics.length === 0) {
      rowMetrics = calculateRowMetrics(
        song.body || [],
        Math.max(0.4, 1 + fontChange * 0.08)
      );
    }
    const newIndicators = computeDOMSplitIntersections(
      paneRefs.current,
      splitMode,
      rowMetrics
    );
    setIndicators(newIndicators);
  }, [splitMode, paneRefs, song.body, fontChange]);

  // Refresh trigger when showSplitIndicators is enabled
  useEffect(() => {
    if (showSplitIndicators) {
      updateIndicators();
    }
  }, [showSplitIndicators, updateIndicators]);

  // Refresh trigger a): isFullscreen prop change
  useEffect(() => {
    updateIndicators();
  }, [isFullscreen, updateIndicators]);

  // Refresh trigger a): fullscreenchange DOM event
  useEffect(() => {
    const handleFullscreenChange = () => {
      updateIndicators();
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, [updateIndicators]);

  // Refresh trigger b): fontChange prop changes
  useEffect(() => {
    updateIndicators();
    const rafId = requestAnimationFrame(() => {
      updateIndicators();
    });
    const timer = setTimeout(() => {
      updateIndicators();
    }, 50);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timer);
    };
  }, [fontChange, updateIndicators]);

  // Refresh trigger c): height / window resize event
  useEffect(() => {
    const handleResize = () => {
      updateIndicators();
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [updateIndicators]);

  // Refresh trigger c): ResizeObserver on pane elements
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      updateIndicators();
    });
    paneRefs.current.forEach((el) => {
      if (el) ro.observe(el);
    });
    return () => {
      ro.disconnect();
    };
  }, [paneRefs, splitMode, updateIndicators]);

  // Refresh trigger d): scroll listeners on pane elements
  useEffect(() => {
    const handleScroll = () => {
      updateIndicators();
    };
    const elements: HTMLElement[] = [];
    paneRefs.current.forEach((el) => {
      if (el) {
        el.addEventListener("scroll", handleScroll, { passive: true });
        elements.push(el);
      }
    });
    return () => {
      elements.forEach((el) => {
        el.removeEventListener("scroll", handleScroll);
      });
    };
  }, [paneRefs, splitMode, updateIndicators]);

  // Refresh trigger on split mode, split page state, and song body changes
  useEffect(() => {
    updateIndicators();
    const rafId = requestAnimationFrame(() => {
      updateIndicators();
    });
    return () => cancelAnimationFrame(rafId);
  }, [splitMode, splitPageState, song.body, updateIndicators]);

  // Real-time pane scroll handler forwarding to onPaneScroll and updating indicators
  const handlePaneScroll = useCallback(
    (index: number) => {
      onPaneScroll(index);
      updateIndicators();
    },
    [onPaneScroll, updateIndicators]
  );

  // Synchronized unified wheel scrolling across all panels with instant boundary clamping
  useEffect(() => {
    const cardEl = lyricsCardRef.current;
    if (!cardEl || !applyUnifiedScrollDelta) return;

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) < 0.1) return;
      e.preventDefault();
      applyUnifiedScrollDelta(e.deltaY);
      updateIndicators();
    };

    cardEl.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      cardEl.removeEventListener("wheel", handleWheel);
    };
  }, [lyricsCardRef, applyUnifiedScrollDelta, updateIndicators]);

  // Synchronized unified touch / drag scrolling across all panels with instant boundary clamping
  useEffect(() => {
    const cardEl = lyricsCardRef.current;
    if (!cardEl || !applyUnifiedScrollDelta) return;

    let touchStartY: number | null = null;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        touchStartY = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartY === null || e.touches.length !== 1) return;
      const currentY = e.touches[0].clientY;
      const deltaY = touchStartY - currentY;
      touchStartY = currentY;
      if (Math.abs(deltaY) > 0.5) {
        e.preventDefault();
        applyUnifiedScrollDelta(deltaY);
        updateIndicators();
      }
    };

    const handleTouchEnd = () => {
      touchStartY = null;
    };

    cardEl.addEventListener("touchstart", handleTouchStart, { passive: true });
    cardEl.addEventListener("touchmove", handleTouchMove, { passive: false });
    cardEl.addEventListener("touchend", handleTouchEnd, { passive: true });
    return () => {
      cardEl.removeEventListener("touchstart", handleTouchStart);
      cardEl.removeEventListener("touchmove", handleTouchMove);
      cardEl.removeEventListener("touchend", handleTouchEnd);
    };
  }, [lyricsCardRef, applyUnifiedScrollDelta, updateIndicators]);

  return (
    <Box mb={0} pb={0}>
      <Card
        ref={lyricsCardRef}
        variant="outlined"
        sx={{
          position: isFullscreen ? "fixed" : "relative",
          ...(isFullscreen && {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100dvh",
            zIndex: 1250,
            borderRadius: 0,
            border: "none",
            m: 0,
          }),
          p: 0,
          pb: 0,
          mb: 0,
          bgcolor: "#ffffff",
          borderRadius: isFullscreen ? 0 : 2,
          borderColor: isFullscreen ? "transparent" : "#e2e8f0",
          boxShadow: isFullscreen ? "none" : "0 1px 3px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Desktop Song Controls Header */}
        <SongControlsDesktop
          visibleContent={visibleContent}
          onVisibleContentChange={onVisibleContentChange}
          isScrolling={isScrolling}
          onToggleScroll={onToggleScroll}
          scrollSpeed={scrollSpeed}
          onSpeedChange={onSpeedChange}
          splitMode={preferredSplitMode ?? splitMode}
          onSplitModeChange={onSplitModeChange}
          splitPageState={splitPageState}
          totalPages={totalPages}
          onPrevPage={onPrevPage}
          onNextPage={onNextPage}
          onIncreaseFont={onIncreaseFont}
          onDecreaseFont={onDecreaseFont}
          onCopyChords={onCopyChords}
          isFullscreen={isFullscreen}
          onToggleFullscreen={onToggleFullscreen}
          showSplitIndicators={showSplitIndicators}
          onToggleSplitIndicators={onToggleSplitIndicators}
        />

        {/* Mobile Song Controls Header */}
        <SongControlsMobile
          visibleContent={visibleContent}
          onVisibleContentChange={onVisibleContentChange}
          isScrolling={isScrolling}
          scrollSpeed={scrollSpeed}
          onToggleScroll={onToggleScroll}
          splitMode={preferredSplitMode ?? splitMode}
          onSplitModeChange={onSplitModeChange}
          splitPageState={splitPageState}
          totalPages={totalPages}
          onPrevPage={onPrevPage}
          onNextPage={onNextPage}
          isFullscreen={isFullscreen}
          onToggleFullscreen={onToggleFullscreen}
          mobileSettingsOpen={mobileSettingsOpen}
          onToggleMobileSettings={onToggleMobileSettings}
          onIncreaseFont={onIncreaseFont}
          onDecreaseFont={onDecreaseFont}
          onCopyChords={onCopyChords}
          onSpeedChange={onSpeedChange}
          showSplitIndicators={showSplitIndicators}
          onToggleSplitIndicators={onToggleSplitIndicators}
        />

        {/* Split Screen Render Panes */}
        {song.body && song.body.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${splitMode}, minmax(0, 1fr))`,
              gap: { xs: 0.5, sm: 1, md: 1.5 },
              width: "100%",
              flex: 1,
              minHeight: 0,
              height: isFullscreen ? "100%" : "auto",
            }}
          >
            {Array.from({ length: splitMode }).map((_, index) => {
              const pageNum = splitPageState.pages[index] ?? (index + 1);
              const isSplitActive = splitMode > 1;

              return (
                <Box
                  key={index}
                  data-testid={`split-column-${index}`}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 1.5,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                    overflow: "hidden",
                    position: "relative",
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  {isSplitActive && (
                    <Box
                      data-testid={`split-pane-header-${index}`}
                      sx={{
                        bgcolor: "#f1f5f9",
                        borderBottom: "1px solid #cbd5e1",
                        px: { xs: 0.75, sm: 1.25 },
                        py: 0.35,
                        display: "flex",
                        alignItems: "center",
                        color: "#334155",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        fontWeight: 600,
                        userSelect: "none",
                      }}
                    >
                      <span>Page {pageNum}</span>
                    </Box>
                  )}

                  <Box
                    sx={{
                      position: "relative",
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <CardContent
                      ref={(el) => {
                        paneRefs.current[index] = el;
                        if (index === 0) {
                          lyricsRef.current = el;
                        }
                      }}
                      data-page-number={pageNum}
                      onScroll={() => handlePaneScroll(index)}
                      sx={{
                        maxHeight: isFullscreen ? "none" : { xs: "75vh", sm: "80vh", md: "84vh" },
                        overflowY: "auto",
                        px: { xs: 0.5, sm: 1 },
                        py: 0.5,
                        pb: 0,
                        flex: 1,
                        position: "relative",
                        "&:last-child": { pb: 0 },
                      }}
                    >
                      <SongBody
                        rows={song.body}
                        fontChange={fontChange}
                        isHebrew={isHebrew}
                        customChords={song.custom_chords}
                        capo={song.capo}
                        songKey={song.key}
                      />
                    </CardContent>

                    {isSplitActive && showSplitIndicators && (
                      <SplitForegroundIndicator
                        indicators={indicators}
                        paneIndex={index}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <CardContent sx={{ p: 3, textAlign: "center" }}>
            <Box display="flex" justifyContent="center" mb={1}>
              <SongCapoKeyBar capo={song.capo} songKey={song.key} isHebrew={isHebrew} />
            </Box>
            <Typography color="text.secondary">
              No chord content available for this song.
            </Typography>
          </CardContent>
        )}
      </Card>
    </Box>
  );
};
